import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import useProcessFlowStore from '../../stores/processFlowStore';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

function FlowSettings({ flow, open, onClose, isCreateMode = false }) {
  const { updateProcessFlow, createProcessFlow, updateCurrentFlow } = useProcessFlowStore();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    name: '',
    description: '',
    category: 'process',
    status: 'draft',
    autoSaveEnabled: true,
    isPublic: false,
    settings: {
      gridEnabled: true,
      snapToGrid: false,
      gridSize: 20,
      connectionLineType: 'default',
      nodesDraggable: true,
      nodesConnectable: true,
      elementsSelectable: true,
      panOnDrag: true,
      zoomOnScroll: true,
      preventScrolling: true
    }
  });

  useEffect(() => {
    if (flow) {
      setSettings({
        name: flow.name || '',
        description: flow.description || '',
        category: flow.category || 'process',
        status: flow.status || 'draft',
        autoSaveEnabled: flow.autoSaveEnabled !== false,
        isPublic: flow.isPublic || false,
        settings: {
          gridEnabled: flow.settings?.gridEnabled !== undefined ? flow.settings.gridEnabled : true,
          snapToGrid: flow.settings?.snapToGrid !== undefined ? flow.settings.snapToGrid : false,
          gridSize: flow.settings?.gridSize || 20,
          connectionLineType: flow.settings?.connectionLineType || 'default',
          nodesDraggable: flow.settings?.nodesDraggable !== undefined ? flow.settings.nodesDraggable : true,
          nodesConnectable: flow.settings?.nodesConnectable !== undefined ? flow.settings.nodesConnectable : true,
          elementsSelectable: flow.settings?.elementsSelectable !== undefined ? flow.settings.elementsSelectable : true,
          panOnDrag: flow.settings?.panOnDrag !== undefined ? flow.settings.panOnDrag : true,
          zoomOnScroll: flow.settings?.zoomOnScroll !== undefined ? flow.settings.zoomOnScroll : true,
          preventScrolling: flow.settings?.preventScrolling !== undefined ? flow.settings.preventScrolling : true
        }
      });
    }
  }, [flow]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSettingChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!settings.name?.trim()) {
        enqueueSnackbar('Name is required', { variant: 'error' });
        return;
      }

      // Validate grid size
      if (settings.settings.gridSize < 10 || settings.settings.gridSize > 50) {
        enqueueSnackbar('Grid size must be between 10 and 50', { variant: 'error' });
        return;
      }

      // Prepare the data payload
      const updateData = {
        name: settings.name.trim(),
        description: settings.description?.trim() || '',
        category: settings.category,
        status: settings.status,
        autoSaveEnabled: Boolean(settings.autoSaveEnabled),
        isPublic: Boolean(settings.isPublic),
        settings: {
          gridEnabled: Boolean(settings.settings.gridEnabled),
          snapToGrid: Boolean(settings.settings.snapToGrid),
          gridSize: Number(settings.settings.gridSize),
          connectionLineType: settings.settings.connectionLineType,
          nodesDraggable: Boolean(settings.settings.nodesDraggable),
          nodesConnectable: Boolean(settings.settings.nodesConnectable),
          elementsSelectable: Boolean(settings.settings.elementsSelectable),
          panOnDrag: Boolean(settings.settings.panOnDrag),
          zoomOnScroll: Boolean(settings.settings.zoomOnScroll),
          preventScrolling: Boolean(settings.settings.preventScrolling)
        }
      };

      if (isCreateMode || !flow?._id) {
        // In create mode, update the local flow
        updateCurrentFlow(updateData);
        enqueueSnackbar('Settings saved locally. Click Save to create the flow.', { variant: 'info' });
      } else {
        // Update existing flow
        await updateProcessFlow(flow._id, updateData);
        enqueueSnackbar('Settings updated successfully', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      console.error('Failed to update settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update settings';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Process Flow Settings
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            General Information
          </Typography>

          <TextField
            label="Name"
            value={settings.name}
            onChange={handleChange('name')}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={settings.description}
            onChange={handleChange('description')}
            multiline
            rows={3}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={settings.category}
              onChange={handleChange('category')}
              label="Category"
            >
              <MenuItem value="planning">Planning</MenuItem>
              <MenuItem value="process">Process</MenuItem>
              <MenuItem value="workflow">Workflow</MenuItem>
              <MenuItem value="documentation">Documentation</MenuItem>
              <MenuItem value="template">Template</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={settings.status}
              onChange={handleChange('status')}
              label="Status"
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="review">Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>

          <Divider />

          <Typography variant="subtitle2">
            Behavior Settings
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.autoSaveEnabled}
                onChange={handleChange('autoSaveEnabled')}
              />
            }
            label="Enable Auto-save"
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.isPublic}
                onChange={handleChange('isPublic')}
              />
            }
            label="Make Public"
          />

          <Divider />

          <Typography variant="subtitle2">
            Editor Settings
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.settings.gridEnabled}
                onChange={handleSettingChange('gridEnabled')}
              />
            }
            label="Show Grid"
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.settings.snapToGrid}
                onChange={handleSettingChange('snapToGrid')}
              />
            }
            label="Snap to Grid"
          />

          <TextField
            label="Grid Size"
            type="number"
            value={settings.settings.gridSize}
            onChange={handleSettingChange('gridSize')}
            fullWidth
            inputProps={{ min: 10, max: 50, step: 5 }}
          />

          <FormControl fullWidth>
            <InputLabel>Connection Line Type</InputLabel>
            <Select
              value={settings.settings.connectionLineType}
              onChange={handleSettingChange('connectionLineType')}
              label="Connection Line Type"
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="straight">Straight</MenuItem>
              <MenuItem value="step">Step</MenuItem>
              <MenuItem value="smoothstep">Smooth Step</MenuItem>
              <MenuItem value="bezier">Bezier</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FlowSettings;